<template>
  <div class="grid md:grid-cols-2 gap-4">
    <TierSelection
      v-if="step === 1"
      :tiers="tiers"
      :selected-tier="selectedTier"
      :loading="tiersLoading"
      @select-tier="selectTier"
      @next="nextStep" />

    <PaymentForm
      v-if="step === 2"
      :selected-tier="selectedTier"
      :processing="subscription.processing.value"
      :payment-successful="subscription.paymentSuccessful.value"
      :error="subscription.error.value"
      @go-back="step = 1"
      @attempt-payment="handlePaymentAttempt" />

    <OrderConfirmation
      v-if="step === 3"
      :selected-tier="selectedTier"
      :user-first-name="userFirstName"
      :applied-discount="appliedDiscount"
      @close="close" />
    <PaymentUpsellPanel />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, onUnmounted, ref } from 'vue'
import { StripeElementsOptions } from '@stripe/stripe-js'

import PaymentForm from '@/components/PaymentModal/PaymentForm.vue'
import TierSelection from '@/components/PaymentModal/TierSelection.vue'
import OrderConfirmation from '@/components/PaymentModal/OrderConfirmation.vue'
import PaymentUpsellPanel from '@/app/components/modals/PaymentUpsellPanel.vue'

import { useStripe } from '@/composables/useStripe.ts'
import { PlanTier, useTieredPlan } from '@/composables/useTieredPlans.ts'
import { useSubscription } from '@/composables/useSubscription.ts'
import type { PromotionDiscount } from '@/composables/useSubscription.ts'
import { useUserStore } from '@/store/pinia/UserStore.ts'
import { useModalStore } from '@/store/pinia/ModalStore.ts'
import { useToast } from '@/composables/useToast.ts'
import { trackButtonClick } from '@/services/AnalyticsService.ts'

const { fetchTieredPlans, tiers, loading: tiersLoading, selectedPlan } = useTieredPlan()
const { mountPaymentElement, unmountPaymentElement, reinitialize } = useStripe()
const subscription = useSubscription()
const userStore = useUserStore()

const selectedTier = ref<PlanTier | null>(null)
const step = ref(1)
const appliedDiscount = ref<any>(null)

const userFirstName = computed(() => {
  return userStore.getUser?.first_name ?? ''
})

const options = {
  mode: 'subscription',
  locale: 'en',
  amount: selectedTier.value?.price.amount ?? 0,
  currency: 'aud',
} as StripeElementsOptions

const selectTier = (value_tier: string) => {
  selectedTier.value = tiers.value.find((item) => item.value_tier === value_tier) || null
}

const nextStep = (tier: PlanTier) => {
  trackButtonClick(
    'payment-modal-next-button',
    'Next',
    {
      tier: tier.value_tier,
      price: (tier.price?.amount ?? 0) / 100,
    },
    'Subscriptions',
  )
  step.value = 2
  reinitialize()
  mountPaymentElement('#payment-element', options)
}

const handlePaymentAttempt = (paymentType: string, promoCode?: string, discount?: PromotionDiscount | null) => {
  appliedDiscount.value = discount

  // Handle coupon-only payments (100% discount)
  if (paymentType === 'coupon') {
    subscription.submitSubscriptionRequest(
      undefined, // no payment method needed
      selectedTier.value,
      promoCode,
      () => (step.value = 3),
    )
    return
  }

  // Handle regular credit card payments (with or without partial discounts)
  subscription.handlePaymentAttempt(
    selectedTier.value,
    promoCode,
    () => (step.value = 3), // onSuccess callback
    discount, // pass the discount parameter
  )
}

const close = () => useModalStore().closeModal()

onBeforeMount(async () => {
  await fetchTieredPlans()

  // Check if user already has active subscription
  const hasActiveSubscription = userStore.getUser?.active_subscription ?? false
  if (hasActiveSubscription) {
    close()
    return
  }

  if (selectedPlan.value && ['basic', 'plus'].includes(selectedPlan.value)) {
    selectTier(selectedPlan.value)
    if (selectedTier.value) {
      nextStep(selectedTier.value)
    }
  } else {
    selectTier('basic')
  }
})

onUnmounted(() => {
  if (step.value === 3) {
    useToast().successToast('Account upgraded.')
  }
  unmountPaymentElement()
})
</script>
